<template>
  <div v-if="zxcvbn !== undefined"
       class="password">
    <BInputGroup>
      <TextInput :id="id"
                 ref="refName"
                 :name="name"
                 :placeholder="placeholder"
                 :state="isSufficientPassword"
                 :type="inputType"
                 :value="modelValue"
                 autocomplete="new-password"
                 @blur="closePasswordRequirements"
                 @focus="openPasswordRequirements"
                 @input="emitValue"></TextInput>
      <div v-cloak
           v-if="badge"
           :class="[isSecureLength ? successClass : '', !isSecureLength && isActive ? errorClass : '']"
           class="password-badge">
        <span v-html="passwordCount"></span>
      </div>
      <BInputGroupAppend>
        <ButtonIconOnly v-if="isPasswordVisible"
                        :disabled="!isActive"
                        :title="tr('common.HIDE_PASSWORD')"
                        :variant="BootstrapVariant.SECONDARY"
                        icon-name="eye"
                        tabindex="-1"
                        @click="togglePasswordVisibility"></ButtonIconOnly>
        <ButtonIconOnly v-else
                        :disabled="!isActive"
                        :title="tr('common.SHOW_PASSWORD')"
                        :variant="BootstrapVariant.SECONDARY"
                        icon-name="eye-blocked"
                        tabindex="-1"
                        @click="togglePasswordVisibility"></ButtonIconOnly>
      </BInputGroupAppend>
    </BInputGroup>
    <div id="strength-meter"
         :class="[strengthMeterClass]">
      <div :class="[strengthMeterFillClass]"
           :data-score="passwordStrength"></div>
    </div>
    <BPopover ref="passwordRequirements"
              :placement="Placement.BOTTOM_LEFT"
              boundary="window"
              target="strength-meter"
              triggers="manual">
      <PasswordRequirements></PasswordRequirements>
    </BPopover>
  </div>
</template>
<script>
  import { BPopover, BInputGroupAppend, BInputGroup } from 'bootstrap-vue';
  import TextInput from '@common/input/TextInput.vue';
  import ButtonIconOnly from '../layout/ButtonIconOnly.vue';
  import translator from '~/zf/frontend/common/mixins/translator';
  import BootstrapVariant from '~/zf/frontend/vendor/bootstrap/BootstrapVariant';
  import PasswordRequirements from './PasswordRequirements.vue';
  import Placement from '~/zf/frontend/common/bootstrapVue/Placement';

  export default {
    name: 'NewPasswordInput',
    compatConfig: {
      MODE: 3,
      COMPONENT_FUNCTIONAL: true,
    },
    components: {
      BPopover,
      BInputGroupAppend,
      BInputGroup,
      TextInput,
      ButtonIconOnly,
      PasswordRequirements,
    },
    mixins: [translator],
    props: {
      modelValue: {
        type: String,
        required: true,
      },
      isValid: {
        type: Boolean,
        required: true,
      },
      blacklist: {
        type: Array,
        default() {
          return [];
        },
      },
      refName: {
        type: String,
        default: 'newPasswordInput',
      },
      id: {
        type: String,
        default: 'password',
      },
      placeholder: {
        type: String,
        default: '',
      },
      name: {
        type: String,
        default: 'password',
      },
      minSecureLength: {
        type: Number,
        default: 10,
      },
      minPasswordScore: {
        type: Number,
        default: 2,
      },
      badge: {
        type: Boolean,
        default: true,
      },
      errorClass: {
        type: String,
        default: 'badge-danger',
      },
      successClass: {
        type: String,
        default: 'badge-success',
      },
      strengthMeterClass: {
        type: String,
        default: 'password-strength-meter',
      },
      strengthMeterFillClass: {
        type: String,
        default: 'password-strength-meter--fill',
      },
    },
    emits: ['update:isValid', 'update:model-value'],
    data() {
      return {
        BootstrapVariant,
        Placement,
        zxcvbn: undefined,
        isPasswordVisible: false,
        maxPasswordLength: 72,
      };
    },
    computed: {
      inputType() {
        return this.isPasswordVisible ? 'text' : 'password';
      },
      passwordScore() {
        const checkedPassword = this.modelValue.slice(0, 75);
        return this.zxcvbn(checkedPassword, this.blacklist).score;
      },
      passwordStrength() {
        return this.modelValue ? this.passwordScore : null;
      },
      isSecureLength() {
        return this.modelValue ? this.modelValue.length >= this.minSecureLength : false;
      },
      isLessOrEqualMaxLength() {
        return this.modelValue ? this.modelValue.length <= this.maxPasswordLength : false;
      },
      isSufficientPassword() {
        const isValid = this.isSecureLength
          && this.isLessOrEqualMaxLength
          && this.passwordScore >= this.minPasswordScore;
        this.$emit('update:isValid', isValid);
        return this.isActive ? isValid : null;
      },
      isActive() {
        return this.modelValue && this.modelValue.length > 0;
      },
      passwordCount() {
        if (this.modelValue) {
          return this.modelValue.length > this.minSecureLength ? `${this.minSecureLength}+` : this.modelValue.length;
        }
        return '&nbsp;';
      },
    },
    async created() {
      this.zxcvbn = await this.loadZxcvbn();
    },
    methods: {
      openPasswordRequirements() {
        this.$refs.passwordRequirements.$emit('open');
      },
      closePasswordRequirements() {
        this.$refs.passwordRequirements.$emit('close');
      },
      emitValue(value) {
        this.$emit('update:model-value', value);
      },
      async loadZxcvbn() {
        const { default: zxcvbn } = await import('zxcvbn');
        return zxcvbn;
      },
      togglePasswordVisibility() {
        this.isPasswordVisible = !this.isPasswordVisible;
      },
    },
  };
</script>

<style lang="scss" scoped>
    :deep([v-cloak]) {
      display: none;
    }

    :deep(.password-strength-meter) {
      position: relative;
      height: 3px;
      background: $gray-650;
      margin: 10px auto 5px;
      border-radius: 3px;
    }

    :deep(.password-strength-meter:before),
    :deep(.password-strength-meter:after) {
      content: '';
      height: inherit;
      background: transparent;
      display: block;
      border-color: $white;
      border-style: solid;
      border-width: 0 5px 0 5px;
      position: absolute;
      width: 43%;
      z-index: 10;
    }

    :deep(.password-strength-meter:before) {
      left: 18%;
    }

    :deep(.password-strength-meter:after) {
      right: 18%;
    }

    :deep(.password-strength-meter--fill) {
      background: transparent;
      height: inherit;
      position: absolute;
      width: 0;
      border-radius: inherit;
      transition: width 0.5s ease-in-out, background 0.25s;
    }

    :deep(.password-strength-meter--fill[data-score='0']) {
      background: $red-500;
      width: 18%;
    }

    :deep(.password-strength-meter--fill[data-score='1']) {
      background: $orange-400;
      width: 39%;
    }

    :deep(.password-strength-meter--fill[data-score='2']) {
      background: $green-200;
      width: 61%;
    }

    :deep(.password-strength-meter--fill[data-score='3']) {
      background: $green-100;
      width: 82%;
    }

    :deep(.password-strength-meter--fill[data-score='4']) {
      background: $success;
      width: 100%;
    }

    #newPassword {
      padding-right: 53px;
    }

    :deep(.password-badge) {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 47px;
      border-radius: 6px;
      width: 35px;
      font-size: 14px;
      padding: 0.4em 0.5em;
      z-index: 3;
      text-align: center;
      margin: auto 0;
      height: fit-content;
    }
</style>
